import { inject, Injectable } from "@angular/core";
import { DspAudienceCreateResponse, StrategyDspAudiencePreset } from "@front/m19-api-client";
import { AccountSelectionService, AudiencesService, DspAudiencesConfig } from "@front/m19-services";
import { catchError, map, Observable, shareReplay, switchMap, take, throwError } from "rxjs";
import { IIcon } from "@front/m19-ui";
import { AjaxResponse } from "rxjs/ajax";

export interface SmartAudiencePresetConfig {
  preset: StrategyDspAudiencePreset;
  title: string;
  description: string;
  icon: IIcon;
  warning?: string;
  beta?: boolean;
  amc?: boolean;
}

const RETARGETING_CONFIG: SmartAudiencePresetConfig = {
  preset: StrategyDspAudiencePreset.RETARGETING,
  title: "preset_retargeting_title",
  description: "preset_retargeting_description",
  icon: "boost",
};

const COMPETITOR_CONFIG: SmartAudiencePresetConfig = {
  preset: StrategyDspAudiencePreset.COMPETITOR,
  title: "preset_competitor_title",
  description: "preset_competitor_description",
  icon: "swords",
};

const HIGH_INTENT_CONFIG: SmartAudiencePresetConfig = {
  preset: StrategyDspAudiencePreset.HIGH_INTENT,
  title: "preset_high_intent_title",
  description: "preset_high_intent_description",
  icon: "search",
};

const CART_ABANDONERS_CONFIG: SmartAudiencePresetConfig = {
  preset: StrategyDspAudiencePreset.CART_ABANDONERS,
  title: "preset_cart_abadoners_title",
  description: "preset_cart_abadoners_description",
  warning: "preset_cart_abadoners_warning",
  icon: "cart",
  amc: true,
};

const CLICKED_NOT_PURCHASED_CONFIG: SmartAudiencePresetConfig = {
  preset: StrategyDspAudiencePreset.CLICKED_NOT_PURCHASED,
  title: "preset_clicked_not_purchased_title",
  description: "preset_clicked_not_purchased_description",
  icon: "mousePointerClick",
  amc: true,
};

const LAPSED_BUYERS_CONFIG: SmartAudiencePresetConfig = {
  preset: StrategyDspAudiencePreset.LAPSED_BUYERS,
  title: "lapsed_buyers_title",
  description: "lapsed_buyers_description",
  icon: "timerReset",
  amc: true,
};

export const SMART_AUDIENCE_PRESETS: SmartAudiencePresetConfig[] = [
  RETARGETING_CONFIG,
  COMPETITOR_CONFIG,
  HIGH_INTENT_CONFIG,
  CART_ABANDONERS_CONFIG,
  CLICKED_NOT_PURCHASED_CONFIG,
  LAPSED_BUYERS_CONFIG,
];

@Injectable({
  providedIn: "root",
})
export class DspSmartAudiencesService {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly audienceService = inject(AudiencesService);

  readonly amcPresetsAvailable$: Observable<boolean> =
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      map((am) => am.dspAdvertiser?.hasAmcInstance === true),
      shareReplay(1),
    );

  getAvailablePresets(): SmartAudiencePresetConfig[] {
    return SMART_AUDIENCE_PRESETS;
  }

  createAudiencesFromSmartAudiencePreset(
    baseName: string,
    asins: string[],
    smartAudiencePreset: StrategyDspAudiencePreset,
  ): Observable<DspAudiencesConfig> {
    return this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      take(1),
      switchMap((am) => {
        return this.audienceService.createDspProductAudience(am, {
          asins,
          audiencePreset: smartAudiencePreset,
          baseName,
        });
      }),
      map((response) => {
        const whitelistAudiences = response.createdDspAudiences?.filter((a) => !a.isBlacklist) ?? [];
        const blacklistAudiences = response.createdDspAudiences?.filter((a) => a.isBlacklist) ?? [];

        const dspAudiencesConfig: DspAudiencesConfig = {
          whitelistAudiences: [whitelistAudiences],
          blacklistAudiences: [blacklistAudiences],
        };
        return dspAudiencesConfig;
      }),
      catchError((err: AjaxResponse<DspAudienceCreateResponse>) => {
        let message: string = "Unknown error occurred while creating audience";
        if (err.response?.erroredDspAudiences) message = err.response.erroredDspAudiences[0].message ?? "";
        return throwError(() => new Error(message));
      }),
    );
  }
}
